import React from "react";
import { Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const getHref = (anchor: string) => {
    return isHomePage ? `#${anchor}` : `/#${anchor}`;
  };

  const handleScrollTo = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    if (isHomePage) {
      e.preventDefault();
      const section = document.querySelector(`#${sectionId}`);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
    // If not on homepage, the default link behavior will navigate to /#sectionId
  };

  return (
    <footer className="bg-neutral-900 text-white py-12">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <h3 className="text-xl font-bold mb-4">Tastermonial</h3>
            <p className="text-neutral-400 mb-6">
              Transform your product's glucose data into verified social proof that sells.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.facebook.com/tastermonial"
                target="_blank"
                rel="nofollow"
                className="text-neutral-400 hover:text-white transition-colors"
                aria-label="Go to our facebook page. Open in a new window"
              >
                <Facebook size={24} />
              </a>
              <a
                href="https://instagram.com/tastermonial"
                target="_blank"
                rel="nofollow"
                className="text-neutral-400 hover:text-white transition-colors"
                aria-label="Go to our instagram page. Open in a new window"
              >
                <Instagram size={24} />
              </a>
              <a
                href="https://x.com/tastermonial"
                target="_blank"
                rel="nofollow"
                className="text-neutral-400 hover:text-white transition-colors"
                aria-label="Go to our X (Twitter) page. Open in a new window"
              >
                <Twitter size={24} />
              </a>
              <a
                href="https://www.youtube.com/@tastermonial"
                target="_blank"
                rel="nofollow"
                className="text-neutral-400 hover:text-white transition-colors"
                aria-label="Go to our youtube page. Open in a new window"
              >
                <Youtube size={24} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Product</h4>
            <ul className="space-y-2">
              <li>
                <a
                  href={getHref("dashboard")}
                  className="text-neutral-400 hover:text-white transition-colors"
                  onClick={(e) => handleScrollTo(e, "dashboard")}
                >
                  Features
                </a>
              </li>
              <li>
                <a
                  href={getHref("pricing")}
                  className="text-neutral-400 hover:text-white transition-colors"
                  onClick={(e) => handleScrollTo(e, "pricing")}
                >
                  Pricing
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-2">
              <li>
                <a href="https://tastermonial.com/pages/founding-story" className="text-neutral-400 hover:text-white transition-colors">
                  About
                </a>
              </li>
              <li>
                <a href="https://tastermonial.com/pages/contact-us" className="text-neutral-400 hover:text-white transition-colors">
                  Contact
                </a>
              </li>
              <li>
                <a href="https://tastermonial.com/policies/privacy-policy" className="text-neutral-400 hover:text-white transition-colors">
                  Privacy
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-neutral-800 mt-12 pt-8 text-center text-neutral-400">
          <p>&copy; {new Date().getFullYear()} Tastermonial. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
